// @ts-nocheck
/**
 * Orders Redux Sagas
 * Side effects for user-triggered actions
 * Uses: infrastructure/api, store/slice actions
 */

import { call, put, takeLatest, takeLeading } from 'redux-saga/effects'
import type { PayloadAction } from '@reduxjs/toolkit'
import { extractErrorMessage } from '@spcc/core'
import { ordersApi } from '../infrastructure/api/orders-api'
import { ordersActions } from './orders.slice'
import type { Order, OrdersFilter, CreateOrderRequest, PaginatedOrders, OrderStats } from '../domain/types'
import type { VariantFilterOption } from './orders.slice'

function* fetchOrdersSaga(action: PayloadAction<OrdersFilter | undefined>) {
  try {
    const result: PaginatedOrders = yield call(ordersApi.getOrdersPaginated, action.payload)
    yield put(ordersActions.fetchOrdersSuccess(result))
    if (result.stats) {
      yield put(ordersActions.setStats(result.stats))
    }
  } catch (error) {
    yield put(ordersActions.fetchOrdersFailure(extractErrorMessage(error)))
  }
}

function* createOrderSaga(action: PayloadAction<CreateOrderRequest>) {
  try {
    const order: Order = yield call(ordersApi.createOrder, action.payload)
    yield put(ordersActions.createOrderSuccess(order))
  } catch (error) {
    yield put(ordersActions.createOrderFailure(extractErrorMessage(error)))
  }
}

function* cancelOrderSaga(action: PayloadAction<{ orderId: string; reason: string }>) {
  try {
    const order: Order = yield call(
      ordersApi.cancelOrder,
      action.payload.orderId,
      action.payload.reason
    )
    yield put(ordersActions.cancelOrderSuccess(order))
  } catch (error) {
    yield put(ordersActions.cancelOrderFailure(extractErrorMessage(error)))
  }
}

function* fetchOrderStatsSaga() {
  try {
    const stats: OrderStats = yield call(ordersApi.getOrderStats)
    yield put(ordersActions.fetchOrderStatsSuccess(stats))
  } catch (error) {
    yield put(ordersActions.fetchOrderStatsFailure(extractErrorMessage(error)))
  }
}

function* bulkCancelOrdersSaga(action: PayloadAction<{ orderIds: string[]; reason: string }>) {
  try {
    const result: {
      successCount: number
      failureCount: number
      succeededOrderIds: string[]
    } = yield call(
      ordersApi.bulkCancelOrders,
      action.payload.orderIds,
      action.payload.reason
    )
    yield put(ordersActions.bulkCancelOrdersSuccess(result))
    yield put(ordersActions.fetchOrderStats())
  } catch (error) {
    yield put(ordersActions.bulkCancelOrdersFailure(extractErrorMessage(error)))
  }
}

function* bulkForceUpdateStatusSaga(
  action: PayloadAction<{
    orderIds: string[]
    newStatus: string
    reason: string
    incidentRef?: string
    affectedOpsNote?: string
    forceJobId: string
  }>
) {
  try {
    const { orderIds, newStatus, reason, incidentRef, affectedOpsNote, forceJobId } = action.payload
    const result: {
      successCount: number
      failureCount: number
      succeededOrderIds: string[]
    } = yield call(
      ordersApi.bulkForceUpdateStatus,
      orderIds,
      newStatus,
      reason,
      incidentRef,
      affectedOpsNote,
      forceJobId
    )
    yield put(ordersActions.bulkForceUpdateStatusSuccess(result))
    // Refresh the orders list so the new statuses appear immediately
    yield put(ordersActions.fetchOrders())
  } catch (error) {
    yield put(ordersActions.bulkForceUpdateStatusFailure(extractErrorMessage(error)))
  }
}

function* fetchVariantOptionsSaga() {
  try {
    const options: VariantFilterOption[] = yield call(ordersApi.getVariantOptions)
    yield put(ordersActions.setVariantFilterOptions(options))
  } catch {
    // silent fail — filter drawer will just show no product options
  }
}

export function* ordersSaga() {
  yield takeLatest(ordersActions.fetchOrders.type, fetchOrdersSaga)
  yield takeLatest(ordersActions.createOrder.type, createOrderSaga)
  yield takeLatest(ordersActions.cancelOrder.type, cancelOrderSaga)
  // takeLeading: ignore subsequent dispatches while the first is in-flight (prevents StrictMode double-mount duplicates)
  yield takeLeading(ordersActions.fetchOrderStats.type, fetchOrderStatsSaga)
  yield takeLatest(ordersActions.bulkCancelOrders.type, bulkCancelOrdersSaga)
  yield takeLatest(ordersActions.bulkForceUpdateStatus.type, bulkForceUpdateStatusSaga)
  yield takeLeading(ordersActions.fetchVariantOptions.type, fetchVariantOptionsSaga)
}
